import { Button } from "@ai-teach/ui-web";
import { useState } from "react";
import { HiDownload } from "react-icons/hi";
import type { WordMasteryDetail } from "../../../generated/graphql";

interface WordsTableProps {
  words: WordMasteryDetail[];
  total: number;
  onSort?: (field: string, order: string) => void;
}

export function WordsTable({ words, total, onSort }: WordsTableProps) {
  const [sortField, setSortField] = useState("dateMastered");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSort = (field: string) => {
    const newOrder = sortField === field && sortOrder === "desc" ? "asc" : "desc";
    setSortField(field);
    setSortOrder(newOrder);
    onSort?.(field, newOrder);
  };

  return (
    <div className="data-table-container">
      <div className="data-table-header">
        <div className="flex items-center justify-between">
          <h4 className="h6">Words Mastered ({total} total)</h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              /* TODO: CSV export */
            }}
          >
            <HiDownload className="mr-1" />
            Export CSV
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto gradient-scrollbar">
        <table className="data-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort("word")}>
                Word {sortField === "word" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th className="sortable" onClick={() => handleSort("dateMastered")}>
                Date Mastered {sortField === "dateMastered" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Retention</th>
              <th>Reviews</th>
              <th>Difficulty</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {words.map((word) => (
              <tr key={word.slideId}>
                <td>
                  <div className="data-table-cell-name">{word.word}</div>
                </td>
                <td>
                  <span className="data-table-cell-caption">
                    {word.dateMastered ? new Date(word.dateMastered).toLocaleDateString() : "—"}
                  </span>
                </td>
                <td>
                  <div className="flex items-center">
                    <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded-full mr-2 max-w-[100px]">
                      <div
                        className={`h-2 rounded-full ${
                          word.currentRetention >= 80
                            ? "bg-success-500"
                            : word.currentRetention >= 60
                              ? "bg-warning-500"
                              : "bg-error-500"
                        }`}
                        style={{ width: `${word.currentRetention}%` }}
                      />
                    </div>
                    <span className="data-table-cell-caption">
                      {word.currentRetention.toFixed(0)}%
                    </span>
                  </div>
                </td>
                <td>{word.reviewCount}</td>
                <td>
                  <span
                    className={`px-2 py-1 text-xs rounded ${
                      word.difficulty >= 7
                        ? "badge-danger"
                        : word.difficulty >= 4
                          ? "badge-accent"
                          : "badge-primary"
                    }`}
                  >
                    {word.difficulty.toFixed(1)}
                  </span>
                </td>
                <td>
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded ${
                      word.state === "REVIEW"
                        ? "badge-primary"
                        : word.state === "RELEARNING"
                          ? "badge-danger"
                          : "badge-secondary"
                    }`}
                  >
                    {word.state}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
